/** SC-06 — the confirm card: Original and Rephrased per shape, then Apply rewrites / Cancel. Nothing is written before Apply. */

import {
  REPHRASE_COPY,
  rephraseAppliedMessage,
  rephraseProposedHeader,
  rephraseUnchangedSummary,
} from "../../../const";
import { changedRewrites, type SlideRewriteCard as Card } from "../types";
import styles from "./SlideRewriteCard.module.css";

export type SlideRewriteDecision = "apply" | "cancel";

export interface SlideRewriteCardProps {
  readonly card: Card;
  readonly onDecision?: (decision: SlideRewriteDecision) => void;
}

/** PowerPoint's \r and \v paragraph breaks render as spaces in HTML; show them as line breaks. */
function asLines(text: string): string {
  return text.replace(/\r\n|[\r\v]/g, "\n");
}

function settledText(card: Card): string | null {
  switch (card.status) {
    case "applied":
      return rephraseAppliedMessage(changedRewrites(card).length);
    case "cancelled":
      return REPHRASE_COPY.CANCELLED;
    case "stale":
      return REPHRASE_COPY.STALE;
    case "failed":
      return REPHRASE_COPY.WRITE_FAILED;
    default:
      return null;
  }
}

export function SlideRewriteCard({ card, onDecision }: SlideRewriteCardProps) {
  const settled = settledText(card);
  const busy = card.status === "applying";
  // Numbered in slide order before splitting, so a shape keeps its number in either list.
  const numbered = card.rewrites.map((r, i) => ({ r, n: i + 1 }));
  const changed = numbered.filter(({ r }) => !r.unchanged);
  const unchanged = numbered.filter(({ r }) => r.unchanged);
  return (
    <section className={styles.card} aria-label="Proposed slide rewrites" data-status={card.status}>
      <p className={styles.heading}>{rephraseProposedHeader(changed.length)}</p>
      <ol className={styles.list}>
        {changed.map(({ r, n }) => (
          <li key={r.id} className={styles.row}>
            <p className={styles.label}>Shape {n}</p>
            <p className={styles.text}>
              <em>Original:</em> {asLines(r.original)}
            </p>
            <p className={styles.text}>
              <em>Rephrased:</em> {r.rewrite}
            </p>
          </li>
        ))}
      </ol>
      {/* Every shape stays on the card (SC-06 AC-1); unchanged ones are folded so Apply isn't buried. */}
      {unchanged.length > 0 && (
        <details className={styles.unchanged}>
          <summary className={styles.summary}>{rephraseUnchangedSummary(unchanged.length)}</summary>
          <ol className={styles.list}>
            {unchanged.map(({ r, n }) => (
              <li key={r.id} className={styles.row} data-unchanged="">
                <p className={styles.label}>Shape {n}</p>
                <p className={styles.text}>{asLines(r.original)}</p>
              </li>
            ))}
          </ol>
        </details>
      )}
      {settled ? (
        <output className={styles.outcome}>{settled}</output>
      ) : (
        <div className={styles.actions}>
          <button type="button" className={styles.button} disabled={busy} onClick={() => onDecision?.("apply")}>
            {busy ? "Applying…" : "Apply rewrites"}
          </button>
          <button type="button" className={styles.button} disabled={busy} onClick={() => onDecision?.("cancel")}>
            Cancel
          </button>
        </div>
      )}
    </section>
  );
}
